
import React, { useState } from 'react';

interface WaitlistCardProps {
  title: string;
  description: string;
  formUrl: string;
  defaultIcon: React.ReactNode;
}

const WaitlistCard: React.FC<WaitlistCardProps> = ({ title, description, formUrl, defaultIcon }) => {
  const handleButtonClick = () => {
    const element = document.getElementById('form-section');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="relative glass p-8 md:p-10 rounded-[2.5rem] border-indigo-500/20 shadow-2xl flex flex-col items-center text-center h-full transition-all duration-300 hover:border-indigo-500/40 hover:-translate-y-2 group">
      {/* Icon container - Scaled on group hover similar to Features session */}
      <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6 border transition-all duration-300 group-hover:scale-110 bg-indigo-600/20 border-indigo-500/30">
        {defaultIcon}
      </div>
      
      <h3 className="text-xl font-bold mb-4 min-h-[3rem] flex items-center">{title}</h3>
      <p className="text-slate-400 mb-8 leading-relaxed text-sm flex-grow">
        {description}
      </p>
      
      <button 
        onClick={handleButtonClick}
        className="w-full px-6 py-4 rounded-2xl font-bold transition-all text-base shadow-xl flex items-center justify-center gap-3 active:scale-95 bg-indigo-600 hover:bg-indigo-500 shadow-indigo-600/30"
      >
        Garantir meu acesso
        <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
        </svg>
      </button>
    </div>
  );
};

export const Waitlist: React.FC = () => {
  const FORM_URL = "https://forms.gle/your-form-link-here";

  const cards = [
    {
      title: "Para o seu futuro",
      description: "Se você está em construção ou transição de carreira, e busca estar pronto para o momento em que surgir a oportunidade está é sua chance!",
      formUrl: FORM_URL,
      icon: (
        <svg className="w-8 h-8 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      )
    },
    {
      title: "Para o futuro da sua empresa",
      description: "Se você pensa no futuro e busca construir times que apoiem a empresa para atingir os objetivos, aqui você formará seu time de sucesso!",
      formUrl: FORM_URL,
      icon: (
        <svg className="w-8 h-8 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      )
    },
    {
      title: "Para o futuro da sua insituição de ensino",
      description: "Se você é uma instituição de ensino que busca formar pessoas para uma carreira em várias fases da vida, estamos aqui para te ajudar em sua missão!",
      formUrl: FORM_URL,
      icon: (
        <svg className="w-8 h-8 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
        </svg>
      )
    }
  ];

  return (
    <section id="waitlist-section" className="pt-24 pb-24 px-4 relative overflow-hidden bg-slate-950 scroll-mt-24">
      {/* Background glow effects */}
      <div className="absolute top-1/4 left-1/4 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-indigo-600/5 blur-[100px] rounded-full pointer-events-none"></div>
      <div className="absolute bottom-1/4 right-1/4 translate-x-1/2 translate-y-1/2 w-[500px] h-[500px] bg-purple-600/5 blur-[100px] rounded-full pointer-events-none"></div>
      
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-bold mb-4">Pronto para construir sua jornada?</h2>
          <p className="text-slate-400 max-w-2xl mx-auto text-lg">
            Escolha o perfil que melhor se adapta aos seus objetivos e junte-se à nossa comunidade.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch">
          {cards.map((card, index) => (
            <WaitlistCard 
              key={index}
              title={card.title}
              description={card.description}
              formUrl={card.formUrl}
              defaultIcon={card.icon}
            />
          ))}
        </div>

        <div className="mt-16 text-center">
          <p className="text-xs text-slate-500">
            Ao clicar em garantir acesso, você será redirecionado para um formulário de qualificação.
          </p>
        </div>
      </div>
    </section>
  );
};
